import { KnowledgeGraph } from "@semantical/modules/knowledge-graph";
import Diagram from "@semantical/modules/diagram/Diagram";

/**
 * Represents an inferred relationship before it's converted to a DiagramRelationship.
 */
export interface InferredRelationshipData {
    subjectUri: string;
    predicateUri: string;
    objectUri: string;
    ruleId: string;
}

/**
 * Interface for inference rules. Each rule encapsulates logic to derive
 * new relationships based on existing ones.
 *
 * Future: This interface allows integration with OWL2 DL reasoners like Pellet.
 */
export interface InferenceRule {
    id: string;
    name: string;
    description: string;

    /**
     * Run inference and return inferred relationship data.
     * @param knowledgeGraph The knowledge graph containing individuals and relationships
     * @param diagram The diagram for visual context
     * @returns Array of inferred relationship data
     */
    infer(knowledgeGraph: KnowledgeGraph, diagram: Diagram): InferredRelationshipData[];
}

/**
 * The InferenceEngine manages a set of inference rules and coordinates
 * relationship inference across the model.
 *
 * Design: Rules are pluggable, allowing for easy addition of new inference
 * patterns without modifying the engine itself.
 */
export class InferenceEngine {
    private rules: InferenceRule[] = [];

    /**
     * Register a new inference rule with the engine.
     */
    registerRule(rule: InferenceRule): void {
        this.rules.push(rule);
        console.debug(`InferenceEngine: Registered rule "${rule.name}"`);
    }

    /**
     * Run all registered rules and return inferred relationships.
     * @param knowledgeGraph The knowledge graph
     * @param diagram The diagram
     * @returns Array of inferred relationship data from all rules
     */
    runInference(knowledgeGraph: KnowledgeGraph, diagram: Diagram): InferredRelationshipData[] {
        const allInferred: InferredRelationshipData[] = [];

        for (const rule of this.rules) {
            try {
                const inferred = rule.infer(knowledgeGraph, diagram);
                console.debug(`InferenceEngine: Rule "${rule.name}" inferred ${inferred.length} relationships`);
                allInferred.push(...inferred);
            } catch (error) {
                console.error(`InferenceEngine: Rule "${rule.name}" failed:`, error);
            }
        }

        return allInferred;
    }

    /**
     * Get all registered rules.
     */
    getRules(): readonly InferenceRule[] {
        return this.rules;
    }

    /**
     * Clear all registered rules.
     */
    clearRules(): void {
        this.rules = [];
    }
}

export default InferenceEngine;
